import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Skeleton,
  Text,
  VStack
} from '@chakra-ui/react';
import CryptoIcons from 'base64-cryptocurrency-icons';
import clsx from 'clsx';
import React, { FC, useMemo } from 'react';
import { useMedia, useToggle } from 'react-use';

import { Icon } from '@app/components/Icon';
import { TokenIcon } from '@app/components/TokenIcon';
import { kFormatter, toOptionalFixed } from '@app/helpers/format';
import { usePairApr } from '@app/hooks/pools/usePairApr';
import { useTokenPriceV3 } from '@app/hooks/token/useTokenPriceV3';
import { PoolTokensCell } from '@app/screens/pools/components/PoolTokensCell';
import { PositionControls } from '@app/screens/positions/components/PositionControls';
import s from '@app/screens/positions/components/PositionsList/PositionsList.module.scss';
import { PairDayData, Position } from '@app/types/pool';
import { usePairAprV2 } from '@app/hooks/pools/usePairAprV2';
import { getSymbol } from '@app/helpers/symbol';
import { useTokenBalance } from '@app/hooks/token/useTokenBalance';

interface Props {
  item: Position;
  dayData: PairDayData[] | undefined;
}

export const PositionsListRow: FC<Props> = ({ item, dayData }) => {
  const isMobile = useMedia('(max-width: 960px)', false);
  const [isOpen, toggleOpen] = useToggle(false);

  const { data: price, isLoading: loadingPrice } = useTokenPriceV3({
    id: 'weth'
  });

  const {
    liquidityTokenBalance,
    pair: {
      token0,
      token1,
      totalSupply,
      reserveETH,
      reserve0,
      reserve1,
      id,
      token1Price,
      token0Price
    }
  } = item;

  const { prevApr, currentApr } = usePairAprV2({
    token0,
    token1,
    reserve0,
    reserve1,
    pairAddress: id,
    token0Price,
    token1Price,
    dayData
  });

  const { balance: balance0 } = useTokenBalance(token0.id, id);
  const { balance: balance1 } = useTokenBalance(token1.id, id);

  const composition = useMemo(() => {
    if (balance0 === undefined || balance1 === undefined) {
      return;
    }

    const amount0 = (+liquidityTokenBalance * balance0) / +totalSupply;
    const amount1 = (+liquidityTokenBalance * balance1) / +totalSupply;

    return {
      amount0,
      amount1
    };
  }, [liquidityTokenBalance, balance0, balance1, totalSupply]);

  const poolShare = useMemo(() => {
    if (!totalSupply || !liquidityTokenBalance) {
      return 0;
    }

    return (+liquidityTokenBalance * 100) / +totalSupply;
  }, [totalSupply, liquidityTokenBalance]);

  const amountValueUSD = useMemo(() => {
    if (!price) {
      return null;
    }

    const liqBalancePercent = (+liquidityTokenBalance * 100) / +totalSupply;
    const liqBalanceEth = (+reserveETH * liqBalancePercent) / 100;

    return kFormatter(liqBalanceEth * +price, 2);
  }, [liquidityTokenBalance, price, totalSupply, reserveETH]);

  const token0Symbol = getSymbol(token0.symbol);
  const token1Symbol = getSymbol(token1.symbol);

  function renderContent() {
    if (0) {
      return (
        <VStack width="100%">
          <HStack width="100%" justify="flex-start" className={s.mobileHeader}>
            <PoolTokensCell token0={token0} token1={token1} />
            <div className={s.share}>{toOptionalFixed(poolShare, 5)}%</div>

            <IconButton aria-label="control" onClick={toggleOpen} ml="auto">
              <Icon name={isOpen ? 'collapseContent' : 'expandContent'} />
            </IconButton>
          </HStack>
          <HStack width="100%" justify="space-between">
            <Text>Amount</Text>
            <VStack alignItems="flex-start" gap={0}>
              <Text fontSize="16px" lineHeight="26px" color="white">
                {liquidityTokenBalance !== '0'
                  ? toOptionalFixed(+liquidityTokenBalance, 5)
                  : '0.00'}
              </Text>
              <Text fontSize="14px" lineHeight="26px" color="gray.4">
                {amountValueUSD ? `$${amountValueUSD}` : 'n/a'}
              </Text>
            </VStack>
          </HStack>
          <HStack width="100%" justify="space-between">
            <Text>Composition</Text>
            <VStack alignItems="flex-start">
              <HStack>
                <TokenIcon symbol={token0Symbol} />
                <Text fontSize="16px" lineHeight="26px" color="white">
                  {composition ? (
                    toOptionalFixed(composition.amount0, 5)
                  ) : (
                    <Skeleton
                      height="26px"
                      width="48px"
                      startColor="neutral.700"
                      endColor="neutral.800"
                    />
                  )}
                </Text>
              </HStack>
              <HStack>
                <TokenIcon symbol={token1Symbol} />
                <Text fontSize="16px" lineHeight="26px" color="white">
                  {composition ? (
                    toOptionalFixed(composition.amount1, 5)
                  ) : (
                    <Skeleton
                      height="26px"
                      width="48px"
                      startColor="neutral.700"
                      endColor="neutral.800"
                    />
                  )}
                </Text>
              </HStack>
            </VStack>
          </HStack>
          <HStack width="100%" justify="space-between">
            <Text>APR</Text>
            <HStack gap="2px">
              {prevApr && (
                <Text fontSize="16px" lineHeight="26px">
                  {toOptionalFixed(prevApr, 2)}%
                </Text>
              )}
              {prevApr && currentApr && (
                <Icon name="chevronRight" className={s.aprChevron} />
              )}
              {currentApr && (
                <Text fontSize="16px" lineHeight="26px" color="orange">
                  {toOptionalFixed(currentApr, 2)}%
                </Text>
              )}
            </HStack>
          </HStack>
          <HStack width="100%" justify="space-between">
            <Text>Pool share</Text>
            <Text fontSize="16px" lineHeight="26px" color="gray.4">
              {toOptionalFixed(poolShare, 5)}%
            </Text>
          </HStack>
          <HStack width="100%" display="none" justify="space-between">
            <Text>Your rewards</Text>
            <Text fontSize="16px" lineHeight="26px" color="gray.4">
              0.00
            </Text>
          </HStack>
        </VStack>
      );
    }

    return (
      <>
        <Flex width="100%" gap="16px" alignItems="center" className={s.header}>
          <PoolTokensCell token0={token0} token1={token1} />

          <div className={s.share}>{toOptionalFixed(poolShare, 5)}%</div>

          <IconButton aria-label="control" onClick={toggleOpen} ml="auto">
            <Icon name={isOpen ? 'collapseContent' : 'expandContent'} />
          </IconButton>
        </Flex>
        {isOpen && (
          <>
            <div className={s.divider} />
            <div className={s.content}>
              <HStack w="100%" justifyContent="space-between" mb="14px">
                <div className={s.label}>Pooled {token0Symbol}</div>
                <div className={s.value}>
                  {composition ? (
                    toOptionalFixed(composition.amount0, 5)
                  ) : (
                    <Skeleton
                      height="21px"
                      width="48px"
                      startColor="gray.1"
                      endColor="gray.3"
                    />
                  )}
                  <TokenIcon symbol={token0.symbol} />
                </div>
              </HStack>
              <HStack w="100%" justifyContent="space-between" mb="14px">
                <div className={s.label}>Pooled {token1Symbol}</div>
                <div className={s.value}>
                  {composition ? (
                    toOptionalFixed(composition.amount1, 5)
                  ) : (
                    <Skeleton
                      height="21px"
                      width="48px"
                      startColor="gray.1"
                      endColor="gray.3"
                    />
                  )}
                  <TokenIcon symbol={token1.symbol} />
                </div>
              </HStack>
              <HStack w="100%" justifyContent="space-between" mb="14px">
                <div className={s.label}>Total Pooled</div>
                <div className={s.value}>
                  {amountValueUSD ? `$${amountValueUSD} USD` : 'n/a'}
                </div>
              </HStack>
              <HStack w="100%" justifyContent="space-between" mb="14px">
                <div className={s.label}>My Share</div>
                <div className={s.value}>{toOptionalFixed(poolShare, 5)}%</div>
              </HStack>
            </div>
            <div className={s.divider} />
            <div className={s.content}>
              <PositionControls data={item} />
            </div>
          </>
        )}
      </>
    );
  }

  return <Box className={clsx(s.row, s.rowMobile)}>{renderContent()}</Box>;
};
